import React, { useState, useRef, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Flex,
  Button,
  Text,
  Icon,
} from "@chakra-ui/react";
import { HiUpload, HiPlus } from "react-icons/hi";
import { IoCloseCircleOutline } from "react-icons/io5";

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const CMultipleFileUpload = ({
  label,
  fileType = "file",
  onChange,
  required = false,
  setValidated,
  validated,
  isMaxFileLimitReached
}) => {
  const [fields, setFields] = useState([]);

  const fileInputRefs = useRef({});

  const handleFileChange = (e, index) => {
    const selectedFile = e.target.files[0];

    if (selectedFile && selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setFields((prevFields) =>
        prevFields.map((field, i) =>
          i === index
            ? {
              ...field,
              file: null,
              error: `File size exceeds ${MAX_FILE_SIZE_MB} MB limit.`,
            }
            : field
        )
      );
      return;
    }

    setFields((prevFields) =>
      prevFields.map((field, i) =>
        i === index
          ? {
            ...field,
            file: selectedFile ? new File([selectedFile], selectedFile.name, {
              type: selectedFile.type,
              lastModified: selectedFile.lastModified
            }) : null,
            error: selectedFile && field.pdfName ? "" : "Both pdfName and file are required.",
          }
          : field
      )
    );
  };

  const handleTextChange = (e, index) => {
    setFields((prevFields) =>
      prevFields.map((field, i) =>
        i === index ? { ...field, pdfName: e.target.value, error: e.target.value && field.file ? "" : field.error } : field
      )
    );
  };

  const handleButtonClick = (index) => {
    fileInputRefs.current[index]?.click();
  };

  const handleRemoveField = (index) => {
    setFields((prevFields) => prevFields.filter((_, i) => i !== index));
    delete fileInputRefs.current[index];
  };

  const handleAddField = () => {
    setValidated(false)
    setFields((prevFields) => [
      ...prevFields,
      { id: Date.now(), file: null, pdfName: "", error: "" },
    ]);
  };

  const handleValidation = () => {
    let isValid = true;
    const updatedFields = fields.map((field) => {
      if (!field.pdfName || !field.file) {
        isValid = false;
        return { ...field, error: "Both pdf name and file are required." };
      }
      return { ...field, error: "" };
    });

    setFields(updatedFields);
    return isValid;
  };

  useEffect(() => {
    if (validated) {
      handleValidation();
    }
  }, [validated]);

  useEffect(() => {
    if (onChange) {
      onChange(fields);
    }
  }, [fields, onChange]);

  return (
    <FormControl>
      <FormLabel fontWeight="bold" fontSize="md" mb="2">
        {label}
      </FormLabel>

      {fields.map((field, index) => (
        <FormControl key={field.id} isInvalid={!!field.error} mb={2}>
          <Flex
            borderRadius="md"
            pt="2"
            bg="white"
            transition="border-color 0.2s"
            align="center"
            gap={2}
          >
            <Input
              type="text"
              placeholder="Enter PDF Name here"
              size="sm"
              flex="5"
              value={field.pdfName}
              onChange={(e) => handleTextChange(e, index)}
            />

            {field.file ? (
              <Button variant="outline" size="sm" w="30%" title={field.file.name}>
                <Text id="pdf_name">{field.file.name}</Text>
              </Button>
            ) : (
              <Button
                variant="outline"
                leftIcon={<HiUpload />}
                size="sm"
                w="30%"
                onClick={() => handleButtonClick(index)}
              >
                Upload file
              </Button>
            )}

            <Icon
              as={IoCloseCircleOutline}
              h={8}
              w={8}
              onClick={() => handleRemoveField(index)}
              cursor="pointer"
              title='Remove File'
              color='red'
            />
          </Flex>

          <FormErrorMessage>{field.error}</FormErrorMessage>

          <Input
            ref={(el) => (fileInputRefs.current[index] = el)}
            type="file"
            hidden
            onChange={(e) => handleFileChange(e, index)}
            accept={fileType === "image" ? "image/*" : fileType === "pdf" ? ".pdf" : undefined}
          />
        </FormControl>
      ))}

      <Button
        leftIcon={<HiPlus />}
        colorScheme="orange"
        size="sm"
        mt={2}
        onClick={handleAddField}
        isDisabled={isMaxFileLimitReached}>
        Add More
      </Button>
    </FormControl>
  );
};

export default CMultipleFileUpload;
